// src/pages/AgendasViaticos.jsx
import axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const AgendasViaticos = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("info"); // info | agenda | gastos
  const [loading, setLoading] = useState(false);

  // ================== CONTRATO ==================
  const [contrato, setContrato] = useState(null);

  // ================== catálogos ==================
  const [regionales, setRegionales] = useState([]);
  const [centros, setCentros] = useState([]);

  // ================== Campos “gris” ==================
  const [direccionGeneralRegional, setDireccionGeneralRegional] = useState("");
  const [dependenciaCentro, setDependenciaCentro] = useState("");
  const [ciudadDepartamentoMunicipio, setCiudadDepartamentoMunicipio] =
    useState("");
  const [contactoNombre, setContactoNombre] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");

  // ================== Info general ==================
  const [ruta, setRuta] = useState("");
  const [entidad, setEntidad] = useState("");
  const [fechaInicioDesplazamiento, setFechaInicioDesplazamiento] =
    useState("");
  const [fechaFinDesplazamiento, setFechaFinDesplazamiento] = useState("");
  const [objetivoDesplazamiento, setObjetivoDesplazamiento] = useState("");

  const supervisorNombre = useMemo(
    () => contrato?.supervisor?.nombre || "—",
    [contrato]
  );
  const supervisorCargo = useMemo(
    () => contrato?.supervisor?.cargo || "—",
    [contrato]
  );
  const ordenadorNombre = useMemo(
    () =>
      contrato?.ordenGasto?.nombre ||
      contrato?.subdirector?.nombre ||
      "—",
    [contrato]
  );
  const ordenadorCargo = useMemo(
    () =>
      contrato?.ordenGasto?.cargo ||
      contrato?.subdirector?.cargo ||
      "—",
    [contrato]
  );

  // ================== Gastos ==================
  const [gastoAereo, setGastoAereo] = useState("");
  const [gastoTerrestre, setGastoTerrestre] = useState("");
  const [gastoIntermunicipal, setGastoIntermunicipal] = useState("");

  // ================== Agenda días ==================
  const blankActividad = useMemo(
    () => ({ horaInicio: "", horaFin: "", actividad: "" }),
    []
  );

  const blankDia = useMemo(
    () => ({
      fecha: "",
      pernocta: false,
      rutaIda: "",
      transporteIda: "",
      actividades: [{ ...blankActividad }],
      rutaRegreso: "",
      transporteRegreso: "",
      observaciones: "",
    }),
    [blankActividad]
  );

  const [agendaDias, setAgendaDias] = useState([{ ...blankDia }]);

  // ================== Obligaciones (dinámicas seleccionadas en el form) ==================
  const blankObligacion = useMemo(() => ({ obligacion: "", detalle: "" }), []);
  const [obligacionesDin, setObligacionesDin] = useState([
    { ...blankObligacion },
  ]);

  // ============================================================
  // ✅ NUEVO: Catálogo REAL de obligaciones (cargado del backend)
  // ============================================================
  const [opcionesObligaciones, setOpcionesObligaciones] = useState([]);
  const [loadingObligaciones, setLoadingObligaciones] = useState(false);

  // ================== Helpers UI ==================
  const stepProgress = useMemo(() => {
    if (activeTab === "info") return 33;
    if (activeTab === "agenda") return 66;
    return 100;
  }, [activeTab]);

  const totalGastos = useMemo(() => {
    const a = Number(gastoAereo || 0);
    const t = Number(gastoTerrestre || 0);
    const i = Number(gastoIntermunicipal || 0);
    return a + t + i;
  }, [gastoAereo, gastoTerrestre, gastoIntermunicipal]);

  const formatCOP = (value) => {
    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    } catch {
      return String(value || 0);
    }
  };

  const toISODate = (dateValue) => {
    if (!dateValue) return "";
    try {
      return new Date(dateValue).toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  // ================== Helpers contrato ==================
  const contratistaNombre = useMemo(() => {
    const inst = contrato?.instructor;
    const nombreCompleto =
      contrato?.instructorNombreCompleto ||
      `${inst?.nombre1Instructor || ""} ${inst?.nombre2Instructor || ""} ${inst?.apellido1Instructor || ""
        } ${inst?.apellido2Instructor || ""}`.trim();
    return nombreCompleto || "—";
  }, [contrato]);

  const contratistaDocumento = useMemo(() => {
    return contrato?.instructor?.documentoInstructor || "—";
  }, [contrato]);

  const contratoNumero = useMemo(() => {
    return (
      contrato?.contrato ||
      contrato?.codigoContrato ||
      contrato?.numeroContrato ||
      contrato?._id ||
      "—"
    );
  }, [contrato]);

  const contratoObjeto = useMemo(() => {
    return (
      contrato?.objetoContrato ||
      contrato?.objetoContractual ||
      contrato?.objeto ||
      "—"
    );
  }, [contrato]);

  const fechaVencimientoContrato = useMemo(() => {
    const f = contrato?.fechaVencimiento || contrato?.fechaFin;
    if (!f) return "—";
    try {
      return new Date(f).toLocaleDateString("es-CO");
    } catch {
      return "—";
    }
  }, [contrato]);

  // ================== Fetch regionales ==================
  useEffect(() => {
    const fetchRegionales = async () => {
      try {
        const res = await axios.get("/api/regionales/listall");
        if (res.data?.ok) {
          const orden = (res.data.regionales || []).sort((a, b) =>
            (a?.nombreRegional || "").localeCompare(b?.nombreRegional || "")
          );
          setRegionales(orden);
        } else {
          setRegionales([]);
        }
      } catch {
        setRegionales([]);
      }
    };
    fetchRegionales();
  }, []);

  // ================== Fetch centros por regional ==================
  const fetchCentrosByRegional = useCallback(async (regionalId) => {
    if (!regionalId) {
      setCentros([]);
      return [];
    }
    try {
      const res = await axios.get(
        `/api/centrosdeformacion/listxregional/${regionalId}`
      );
      if (res.data?.ok) {
        const list = res.data?.regional?.centros || [];
        setCentros(list);
        return list;
      }
      setCentros([]);
      return [];
    } catch {
      setCentros([]);
      return [];
    }
  }, []);

  // ================== Cargar contrato ==================
  useEffect(() => {
    const fetchContrato = async () => {
      if (!id) return;

      try {
        setLoading(true);

        let data;
        try {
          const resCodigo = await axios.get(`/api/contratos/listbycodigo/${id}`);
          data = resCodigo.data;
        } catch {
          const resId = await axios.get(`/api/contratos/listid/${id}`);
          data = resId.data;
        }

        if (!data?.ok) {
          Swal.fire(
            "Atención",
            data?.message || "No se pudo cargar el contrato",
            "warning"
          );
          setContrato(null);
          return;
        }

        const c = data.contrato || null;
        setContrato(c);

        const regionalId = c?.regional || "";
        const centroId = c?.centroFormacion || "";

        let regionalName = "";
        if (regionalId && regionales.length) {
          regionalName =
            regionales.find((r) => r._id === regionalId)?.nombreRegional || "";
        }

        let centroName = "";
        if (regionalId) {
          const centrosList = await fetchCentrosByRegional(regionalId);
          centroName =
            (centrosList || []).find((x) => x._id === centroId)?.nombreCentro ||
            "";
        }

        setDireccionGeneralRegional(regionalName || "");
        setDependenciaCentro(centroName || "");
        setCiudadDepartamentoMunicipio(c?.municipioSede || "");

        setContactoNombre(c?.contacto?.nombre || "");
        setContactoEmail(c?.contacto?.email || "");

        setRuta(c?.ruta || "");
        setEntidad(c?.entidad || "");
        setFechaInicioDesplazamiento(toISODate(c?.fechaInicioDesplazamiento) || "");
        setFechaFinDesplazamiento(toISODate(c?.fechaFinDesplazamiento) || "");
        setObjetivoDesplazamiento(c?.objetivoDesplazamiento || "");
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Error",
          err?.response?.data?.message || "No se pudo cargar el contrato",
          "error"
        );
        setContrato(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContrato();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, regionales, fetchCentrosByRegional]);

  // ============================================================
  // ✅ AJUSTE SOLICITADO: Cargar obligaciones existentes del contrato
  // Endpoint: /api/obligaciones/contrato/:id
  // Se usan para poblar el select (catálogo real)
  // ============================================================
  useEffect(() => {
    const obtenerObligaciones = async () => {
      if (!id) return;

      try {
        setLoadingObligaciones(true);
        const res = await axios.get(`/api/obligaciones/contrato/${id}`);
        const datos = res.data;

        // OJO: algunos backends usan "success" o "status"
        const ok = datos?.ok ?? datos?.success ?? datos?.status;

        if (!ok) {
          console.warn("⚠️ Backend respondió sin OK:", datos);
          setOpcionesObligaciones(["OTRA (especificar)"]);
          return;
        }

        // ✅ detectar array venga como venga
        const arr =
          datos?.obligaciones ||
          datos?.data ||
          datos?.results ||
          datos?.payload ||
          [];

        if (!Array.isArray(arr)) {
          console.warn("⚠️ 'obligaciones' no es array:", arr);
          setOpcionesObligaciones(["OTRA (especificar)"]);
          return;
        }

        // ✅ detectar el campo correcto automáticamente
        const lista = arr
          .map((item) => {
            return (
              item?.descripcion ||
              item?.obligacion ||
              item?.nombre ||
              item?.titulo ||
              item?.observacion || // por si en tu modelo guardan ahí
              ""
            );
          })
          .map((s) => String(s).trim())
          .filter(Boolean);

        const unicasOrdenadas = Array.from(new Set(lista)).sort((a, b) =>
          a.localeCompare(b, "es")
        );

        setOpcionesObligaciones([...unicasOrdenadas, "OTRA (especificar)"]);
      } catch (error) {
        console.error("❌ Error al obtener obligaciones:", error);

        setOpcionesObligaciones(["OTRA (especificar)"]);
        Swal.fire("Error", "No se pudieron cargar las obligaciones.", "error");
      } finally {
        setLoadingObligaciones(false);
      }
    };

    obtenerObligaciones();
  }, [id]);

  // ================== CLEAN ==================
  const cleanData = () => {
    setActiveTab("info");

    setDireccionGeneralRegional("");
    setDependenciaCentro("");
    setCiudadDepartamentoMunicipio("");
    setContactoNombre("");
    setContactoEmail("");

    setRuta("");
    setEntidad("");
    setFechaInicioDesplazamiento("");
    setFechaFinDesplazamiento("");
    setObjetivoDesplazamiento("");

    setGastoAereo("");
    setGastoTerrestre("");
    setGastoIntermunicipal("");

    setAgendaDias([{ ...blankDia }]);
    setObligacionesDin([{ ...blankObligacion }]);
  };

  // ================== AGENDA DÍAS ==================
  const handleAddDia = () => {
    if (agendaDias.length >= 5) {
      Swal.fire("Límite", "Máximo 5 días por agenda.", "info");
      return;
    }
    setAgendaDias((prev) => [...prev, { ...blankDia }]);
  };

  const handleRemoveDia = (index) => {
    setAgendaDias((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ ...blankDia }];
    });
  };

  const handleDiaChange = (index, field, value) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddActividad = (diaIndex) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];

      const acts = Array.isArray(dia.actividades) ? [...dia.actividades] : [];
      if (acts.length >= 10) return prev;

      const nueva = { ...blankActividad };
      if (acts.length > 0 && acts[acts.length - 1]?.horaFin)
        nueva.horaInicio = acts[acts.length - 1].horaFin;

      acts.push(nueva);
      updated[diaIndex] = { ...dia, actividades: acts };
      return updated;
    });
  };

  const handleActividadChange = (diaIndex, actIndex, field, value) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];
      const acts = [...(dia.actividades || [])];

      acts[actIndex] = { ...(acts[actIndex] || {}), [field]: value };

      if (field === "horaFin") {
        const siguiente = acts[actIndex + 1];
        if (siguiente) acts[actIndex + 1] = { ...siguiente, horaInicio: value };
      }

      updated[diaIndex] = { ...dia, actividades: acts };
      return updated;
    });
  };

  const handleRemoveActividad = (diaIndex, actIndex) => {
    setAgendaDias((prev) => {
      const updated = [...prev];
      const dia = updated[diaIndex];

      let acts = (dia.actividades || []).filter((_, i) => i !== actIndex);
      if (acts.length === 0) acts = [{ ...blankActividad }];

      updated[diaIndex] = { ...dia, actividades: acts };
      return updated;
    });
  };

  // ================== OBLIGACIONES ==================
  const addObligacion = () => {
    if (obligacionesDin.length >= 15) {
      Swal.fire("Límite", "Máximo 15 obligaciones.", "info");
      return;
    }
    setObligacionesDin((prev) => [...prev, { ...blankObligacion }]);
  };

  const removeObligacion = (idx) => {
    setObligacionesDin((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [{ ...blankObligacion }];
    });
  };

  const changeObligacion = (idx, field, value) => {
    setObligacionesDin((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  // ================== SUBMIT ==================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const invalidoFormato =
      !direccionGeneralRegional.trim() ||
      !dependenciaCentro.trim() ||
      !ciudadDepartamentoMunicipio.trim() ||
      !contactoNombre.trim() ||
      !contactoEmail.trim();

    const invalidoInfo =
      !ruta.trim() ||
      !entidad.trim() ||
      !fechaInicioDesplazamiento ||
      !fechaFinDesplazamiento ||
      !objetivoDesplazamiento.trim();

    const invalidoDias = agendaDias.some((dia) => {
      if (!dia.fecha) return true;
      if (
        !dia.rutaIda.trim() ||
        !dia.transporteIda.trim() ||
        !dia.rutaRegreso.trim() ||
        !dia.transporteRegreso.trim()
      )
        return true;
      if (!Array.isArray(dia.actividades) || dia.actividades.length === 0)
        return true;
      return dia.actividades.some(
        (a) => !a.horaInicio || !a.horaFin || !a.actividad?.trim()
      );
    });

    const invalidoObl = obligacionesDin.some((o) => !o.obligacion?.trim());

    if (invalidoFormato || invalidoInfo || invalidoDias || invalidoObl) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Completa los campos del formato (gris), la información general, la agenda por día y las obligaciones.",
      });
      return;
    }

    const payload = {
      contratoCodigo: id,

      direccionGeneralRegional,
      dependenciaCentro,
      ciudadDepartamentoMunicipio,
      contacto: {
        nombre: contactoNombre,
        email: contactoEmail,
      },

      ruta,
      entidad,
      fechaInicioDesplazamiento,
      fechaFinDesplazamiento,
      objetivoDesplazamiento,

      obligacionesContractuales: obligacionesDin,
      agendaDias,

      gastos: {
        aereo: Number(gastoAereo || 0),
        terrestre: Number(gastoTerrestre || 0),
        intermunicipalCOP: Number(gastoIntermunicipal || 0),
        totalCOP: totalGastos,
      },
    };

    try {
      setLoading(true);

      console.log("Payload agenda:", payload);

      Swal.fire({
        icon: "success",
        title: "Agenda lista",
        text: "Se validó y armó el payload correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la agenda.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="container-flex">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Agenda de Viáticos</h5>
                <small className="text-secondary">
                  Se cargan datos del contrato y se muestran en el formato.
                  Completa lo faltante.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>Volver
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={cleanData}
                  disabled={loading}
                >
                  <i className="fa-solid fa-eraser me-2"></i>Limpiar
                </button>
              </div>
            </div>

            {/* Steps + progress */}
            <div className="mt-3">
              <div className="row g-2">
                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "info" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => setActiveTab("info")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-circle-info me-2"></i>
                    Información
                  </button>
                </div>

                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "agenda" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => setActiveTab("agenda")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-calendar-days me-2"></i>
                    Agenda
                    <span className="badge text-bg-secondary ms-2">
                      {agendaDias.length}/5
                    </span>
                  </button>
                </div>

                <div className="col-12 col-md-auto">
                  <button
                    type="button"
                    className={`btn btn-sm w-100 ${activeTab === "gastos" ? "btn-dark" : "btn-outline-dark"
                      }`}
                    onClick={() => setActiveTab("gastos")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-coins me-2"></i>
                    Gastos
                  </button>
                </div>

                <div className="col-12 col-md">
                  <div className="h-100 d-flex align-items-center">
                    <div className="progress w-100" style={{ height: 8 }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${stepProgress}%` }}
                        aria-valuenow={stepProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-viaticos"
              onSubmit={handleSubmit}
              className="needs-validation"
              noValidate
            >
              {/* ================== TAB INFO ================== */}
              {activeTab === "info" && (
                <div>
                  <div className="px-3 px-md-4 py-3 border-bottom">
                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-md">
                        <div className="fw-semibold">
                          <i className="fa-regular fa-file-lines me-2"></i>
                          Información general
                        </div>
                        <div className="small text-secondary">
                          Se muestra el formato y debajo los campos editables.
                        </div>
                      </div>

                      <div className="col-12 col-md-auto">
                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                          <span className="badge text-bg-dark">
                            <i className="fa-solid fa-file-contract me-2"></i>
                            {contratoNumero || "—"}
                          </span>

                          <span className="badge text-bg-light border">
                            <i className="fa-regular fa-calendar-check me-2"></i>
                            Vence: <b>{fechaVencimientoContrato || "—"}</b>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 px-md-4 pb-4">
                    {/* Sección: Contratista + Contrato */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div className="fw-semibold">
                          <i className="fa-solid fa-id-card me-2"></i>
                          Contratista y contrato
                        </div>

                        <span className="badge text-bg-light border text-wrap">
                          <i className="fa-solid fa-user me-2"></i>
                          {contratistaNombre || "—"}
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-lg-6">
                          <div className="bg-white border rounded-4 p-3 p-md-4 h-100">
                            <div className="small text-secondary">
                              Nombre completo
                            </div>
                            <div className="fw-semibold text-uppercase text-break">
                              {contratistaNombre || "—"}
                            </div>

                            <div className="row g-3 mt-1">
                              <div className="col-12 col-md-4">
                                <div className="small text-secondary">Tipo</div>
                                <div className="fw-semibold">
                                  {contrato?.tipoIdentificacion || "C.C."}
                                </div>
                              </div>
                              <div className="col-12 col-md-8">
                                <div className="small text-secondary">
                                  Documento
                                </div>
                                <div className="fw-semibold text-break">
                                  {contratistaDocumento || "—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-lg-6">
                          <div className="bg-white border rounded-4 p-3 p-md-4 h-100">
                            <div className="row g-3">
                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">Número</div>
                                <div className="fw-semibold text-uppercase text-break">
                                  {contratoNumero || "—"}
                                </div>
                              </div>

                              <div className="col-12 col-md-6">
                                <div className="small text-secondary">
                                  Vencimiento
                                </div>
                                <div className="fw-semibold text-uppercase">
                                  {fechaVencimientoContrato || "—"}
                                </div>
                              </div>

                              <div className="col-12">
                                <div className="small text-secondary">
                                  Objeto contractual
                                </div>
                                <div className="text-uppercase text-break">
                                  {contratoObjeto || "—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr className="my-4" />

                    {/* ================== BLOQUE GRIS (EDITABLES) ================== */}
                    <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary mb-4">
                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Ciudad / Municipio / Departamento *
                          </label>
                          <input
                            type="text"
                            className="form-control border"
                            value={ciudadDepartamentoMunicipio}
                            onChange={(e) =>
                              setCiudadDepartamentoMunicipio(e.target.value)
                            }
                            placeholder="Ej: El Carmen de Viboral"
                            required
                            disabled={loading}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Contacto (Nombre) *
                          </label>
                          <input
                            type="text"
                            className="form-control border"
                            value={contactoNombre}
                            onChange={(e) => setContactoNombre(e.target.value)}
                            placeholder="Ej: Isabel Cristina Trujillo"
                            required
                            disabled={loading}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Contacto (Correo) *
                          </label>
                          <input
                            type="email"
                            className="form-control border"
                            value={contactoEmail}
                            onChange={(e) => setContactoEmail(e.target.value)}
                            placeholder="correo@sena.edu.co"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ================== INFO GENERAL ================== */}
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Ruta *</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-solid fa-route"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={ruta}
                            onChange={(e) => setRuta(e.target.value)}
                            placeholder="Ej: Medellín → Rionegro → Medellín"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Entidad o Empresa *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-solid fa-building"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={entidad}
                            onChange={(e) => setEntidad(e.target.value)}
                            placeholder="Ej: Recinto Quirama"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Fecha inicio del desplazamiento *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-regular fa-calendar"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            value={fechaInicioDesplazamiento}
                            onChange={(e) =>
                              setFechaInicioDesplazamiento(e.target.value)
                            }
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          Fecha fin del desplazamiento *
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="fa-regular fa-calendar-check"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            value={fechaFinDesplazamiento}
                            onChange={(e) =>
                              setFechaFinDesplazamiento(e.target.value)
                            }
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Objetivo del desplazamiento *
                        </label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={objetivoDesplazamiento}
                          onChange={(e) =>
                            setObjetivoDesplazamiento(e.target.value)
                          }
                          placeholder="Describe el objetivo del desplazamiento..."
                          required
                          disabled={loading}
                        />
                      </div>

                      {/* ================== OBLIGACIONES ================== */}
                      <div className="col-12 pt-2">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mb-3">
                          <div>
                            <label className="form-label fw-semibold mb-0">
                              Obligaciones Contractuales <span className="text-danger">*</span>
                            </label>
                            <div className="small text-secondary">
                              Selecciona la obligación; si es “OTRA”, escribe el detalle.
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={addObligacion}
                            disabled={loading}
                            title="Agregar obligación"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="fa-solid fa-plus me-2"></i>
                            Agregar
                          </button>
                        </div>

                        {loadingObligaciones && (
                          <div className="alert alert-light border py-2 mb-3 d-flex align-items-center gap-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="small text-secondary mb-0">Cargando obligaciones del contrato…</span>
                          </div>
                        )}

                        <div className="d-flex flex-column gap-3">
                          {obligacionesDin.map((o, idx) => (
                            <div
                              key={`obl-${idx}`}
                              className="border rounded-4 p-3 bg-body-tertiary"
                            >
                              <div className="row g-2 align-items-start">
                                {/* SELECT */}
                                <div className="col-12 col-md-9">

                                  <select
                                    className="form-select w-100"
                                    value={o.obligacion}
                                    onChange={(e) =>
                                      changeObligacion(idx, "obligacion", e.target.value)
                                    }
                                    required
                                    disabled={loading || loadingObligaciones}
                                  >
                                    <option value="">Seleccione una obligación…</option>

                                    {opcionesObligaciones.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>

                                  {/* ✅ Vista “bonita” del texto completo sin dañar el layout */}
                                  {o.obligacion && o.obligacion !== "OTRA (especificar)" && (
                                    <div className="small text-secondary mt-2">
                                      <span className="fw-semibold">Seleccionada:</span>{" "}
                                      <span className="text-break">{o.obligacion}</span>
                                    </div>
                                  )}
                                </div>

                                {/* BOTÓN QUITAR */}
                                <div className="col-12 col-md-3 d-flex">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger w-100"
                                    onClick={() => removeObligacion(idx)}
                                    disabled={loading}
                                    title="Quitar"
                                    style={{ height: 38 }}
                                  >
                                    <i className="fa-solid fa-trash me-2"></i>
                                    Quitar
                                  </button>
                                </div>

                                {/* DETALLE SI ES OTRA */}
                                {o.obligacion === "OTRA (especificar)" && (
                                  <div className="col-12 mt-1">
                                    <label className="form-label fw-semibold mb-1">Detalle</label>
                                    <input
                                      className="form-control"
                                      value={o.detalle}
                                      onChange={(e) => changeObligacion(idx, "detalle", e.target.value)}
                                      placeholder="Especifique la obligación..."
                                      disabled={loading}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-dark"
                            onClick={() => setActiveTab("agenda")}
                            disabled={loading}
                          >
                            Continuar <i className="fa-solid fa-arrow-right ms-2"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ================== TAB AGENDA ================== */}
              {activeTab === "agenda" && (
                <div>
                  {/* HEADER RESPONSIVE */}
                  <div className="card-header bg-white border-0 pb-0">
                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-md">
                        <h6 className="fw-semibold mb-0">Agenda detallada</h6>
                        <small className="text-secondary">
                          Día, ruta ida/regreso, transporte y actividades con horas.
                        </small>
                      </div>

                      <div className="col-12 col-md-auto">
                        <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end">
                          <button
                            type="button"
                            className="btn btn-primary w-100 w-md-auto"
                            onClick={handleAddDia}
                            disabled={loading || agendaDias.length >= 5}
                          >
                            <i className="fa-solid fa-calendar-plus me-2"></i>
                            Agregar día
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-dark w-100 w-md-auto"
                            onClick={() => setActiveTab("gastos")}
                            disabled={loading}
                          >
                            Gastos <i className="fa-solid fa-arrow-right ms-2"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="card-body pt-3">
                    {agendaDias.map((dia, index) => {
                      const actsCount = Array.isArray(dia.actividades)
                        ? dia.actividades.length
                        : 0;

                      return (
                        <div
                          key={`dia-${index}`}
                          className="border rounded-4 p-3 p-md-4 mb-4"
                        >
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="badge text-bg-dark rounded-pill">
                                <i className="fa-regular fa-calendar me-2"></i>Día{" "}
                                {index + 1}
                              </span>

                              <span className="badge text-bg-light border rounded-pill">
                                <i className="fa-regular fa-clock me-2"></i>
                                {actsCount} actividad
                                {actsCount === 1 ? "" : "es"}
                              </span>
                            </div>

                            <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center justify-content-md-end">
                              <select
                                className="form-select"
                                value={dia.pernocta ? "Sí" : "No"}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "pernocta",
                                    e.target.value === "Sí"
                                  )
                                }
                                disabled={loading}
                                title="Pernocta"
                              >
                                <option value="No">Pernocta: No</option>
                                <option value="Sí">Pernocta: Sí</option>
                              </select>

                              {agendaDias.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger w-100 w-md-auto"
                                  onClick={() => handleRemoveDia(index)}
                                  disabled={loading}
                                >
                                  <i className="fa-solid fa-trash me-2"></i>
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </div>

                          <hr className="my-3" />

                          <div className="row g-3">
                            <div className="col-12 col-md-3">
                              <label className="form-label fw-semibold">
                                Fecha *
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={dia.fecha}
                                onChange={(e) =>
                                  handleDiaChange(index, "fecha", e.target.value)
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-5">
                              <label className="form-label fw-semibold">
                                Ruta de ida *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.rutaIda}
                                onChange={(e) =>
                                  handleDiaChange(index, "rutaIda", e.target.value)
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-4">
                              <label className="form-label fw-semibold">
                                Transporte (ida) *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.transporteIda}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "transporteIda",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-5">
                              <label className="form-label fw-semibold">
                                Ruta de regreso *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.rutaRegreso}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "rutaRegreso",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>

                            <div className="col-12 col-md-4">
                              <label className="form-label fw-semibold">
                                Transporte (regreso) *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={dia.transporteRegreso}
                                onChange={(e) =>
                                  handleDiaChange(
                                    index,
                                    "transporteRegreso",
                                    e.target.value
                                  )
                                }
                                required
                                disabled={loading}
                              />
                            </div>
                          </div>

                          {/* Actividades */}
                          <div className="mt-4">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mb-2">
                              <div>
                                <div className="fw-semibold">Actividades *</div>
                                <div className="small text-secondary">
                                  Hora inicio, hora fin y actividad.
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-primary w-100 w-md-auto"
                                onClick={() => handleAddActividad(index)}
                                disabled={loading || actsCount >= 10}
                              >
                                <i className="fa-solid fa-plus me-2"></i>
                                Agregar actividad
                              </button>
                            </div>

                            <div className="d-none d-md-block table-responsive">
                              <table className="table table-sm align-middle mb-2">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 140 }}>Hora inicio</th>
                                    <th style={{ width: 140 }}>Hora fin</th>
                                    <th>Actividad</th>
                                    <th className="text-end" style={{ width: 120 }}>
                                      Acción
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {(dia.actividades || []).map((act, aIdx) => (
                                    <tr key={`dia-${index}-act-${aIdx}`}>
                                      <td>
                                        <input
                                          type="time"
                                          className="form-control form-control-sm"
                                          value={act.horaInicio}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "horaInicio",
                                              e.target.value
                                            )
                                          }
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          type="time"
                                          className="form-control form-control-sm"
                                          value={act.horaFin}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "horaFin",
                                              e.target.value
                                            )
                                          }
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={act.actividad}
                                          onChange={(e) =>
                                            handleActividadChange(
                                              index,
                                              aIdx,
                                              "actividad",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Ej: Reunión / Visita técnica / Ejecución..."
                                          required
                                          disabled={loading}
                                        />
                                      </td>

                                      <td className="text-end">
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() =>
                                            handleRemoveActividad(index, aIdx)
                                          }
                                          disabled={loading}
                                          title="Eliminar"
                                        >
                                          <i className="fa-solid fa-trash"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div className="d-md-none d-flex flex-column gap-3">
                              {(dia.actividades || []).map((act, aIdx) => (
                                <div
                                  key={`mobile-${index}-act-${aIdx}`}
                                  className="border rounded-4 p-3 bg-light"
                                >
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <label className="small text-secondary">
                                        Inicio
                                      </label>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={act.horaInicio}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "horaInicio",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-6">
                                      <label className="small text-secondary">
                                        Fin
                                      </label>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        value={act.horaFin}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "horaFin",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-12">
                                      <label className="small text-secondary">
                                        Actividad
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={act.actividad}
                                        onChange={(e) =>
                                          handleActividadChange(
                                            index,
                                            aIdx,
                                            "actividad",
                                            e.target.value
                                          )
                                        }
                                        disabled={loading}
                                      />
                                    </div>

                                    <div className="col-12 text-end">
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm w-100"
                                        onClick={() =>
                                          handleRemoveActividad(index, aIdx)
                                        }
                                        disabled={loading}
                                      >
                                        <i className="fa-solid fa-trash me-2"></i>
                                        Eliminar actividad
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="alert alert-light border mb-0 small text-secondary mt-2">
                              <i className="fa-regular fa-circle-check me-2"></i>
                              Tip: al poner “Hora fin”, se autocompleta la “Hora inicio” de la siguiente fila.
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="form-label fw-semibold">
                              Observaciones (opcional)
                            </label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={dia.observaciones}
                              onChange={(e) =>
                                handleDiaChange(
                                  index,
                                  "observaciones",
                                  e.target.value
                                )
                              }
                              placeholder="Notas / novedades / evidencias…"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      );
                    })}

                    <div className="d-flex flex-column flex-md-row gap-2 justify-content-between">
                      <button
                        type="button"
                        className="btn btn-outline-dark w-100 w-md-auto"
                        onClick={() => setActiveTab("info")}
                        disabled={loading}
                      >
                        <i className="fa-solid fa-arrow-left me-2"></i>Volver a
                        información
                      </button>

                      <button
                        type="button"
                        className="btn btn-dark w-100 w-md-auto"
                        onClick={() => setActiveTab("gastos")}
                        disabled={loading}
                      >
                        Continuar a gastos{" "}
                        <i className="fa-solid fa-arrow-right ms-2"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ================== TAB GASTOS ================== */}
              {activeTab === "gastos" && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 pb-0">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <h6 className="fw-semibold mb-0">Liquidación de gastos</h6>
                        <small className="text-secondary">
                          Los valores se envían como números (COP).
                        </small>
                      </div>

                      <span className="badge text-bg-light border">
                        <i className="fa-solid fa-calculator me-2"></i>
                        Total: {formatCOP(totalGastos)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body pt-3">
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Gasto Aéreo (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoAereo}
                          onChange={(e) => setGastoAereo(e.target.value)}
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Gasto Terrestre (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoTerrestre}
                          onChange={(e) => setGastoTerrestre(e.target.value)}
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">
                          Intermunicipal (COP) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={gastoIntermunicipal}
                          onChange={(e) => setGastoIntermunicipal(e.target.value)}
                          min="0"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="alert alert-info mt-3 mb-0">
                      <i className="fa-solid fa-circle-info me-2"></i>
                      Si un gasto no aplica, deja el valor en <b>0</b>. Total:{" "}
                      <b>{formatCOP(totalGastos)}</b>.
                    </div>

                    <div className="d-flex flex-wrap gap-2 justify-content-between mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={() => setActiveTab("agenda")}
                        disabled={loading}
                      >
                        <i className="fa-solid fa-arrow-left me-2"></i>Volver a
                        agenda
                      </button>

                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={cleanData}
                          disabled={loading}
                        >
                          <i className="fa-solid fa-xmark me-2"></i>Cancelar
                        </button>

                        <button
                          type="submit"
                          form="form-viaticos"
                          className="btn btn-dark"
                          disabled={loading}
                        >
                          <i
                            className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                              } me-2`}
                          ></i>
                          Guardar Agenda
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 text-center text-secondary small">
                Verifica la información antes de guardar. (Agenda: {agendaDias.length} día
                {agendaDias.length === 1 ? "" : "s"})
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendasViaticos;